/**
 * UpdateSecretModal Component
 *
 * Modal for updating a secret (name, description, or value).
 *
 * SECURITY NOTES:
 * - Secret values are encrypted via tenant KMS key on backend
 * - Values are write-only (never retrieved via API)
 * - Updating value is optional - if empty, only metadata is updated
 */

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Alert } from '../../../components/ui/Alert';
import { FormField } from '../../../components/forms/FormField';
import { CopyableCode } from '../../../components/ui/CopyableCode';
import { useUpdateSecret } from '../hooks/useUpdateSecret';
import { updateSecretFormSchema, type UpdateSecretFormData } from '../../../lib/validators/secret';
import type { SecretSanitized } from '../../../types/secret.types';

interface UpdateSecretModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  secret: SecretSanitized;
}

/**
 * UpdateSecretModal component
 */
export const UpdateSecretModal: React.FC<UpdateSecretModalProps> = ({
  isOpen,
  onClose,
  tenantId,
  secret,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showValue, setShowValue] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UpdateSecretFormData>({
    resolver: zodResolver(updateSecretFormSchema),
    defaultValues: {
      secretName: secret.secretName,
      description: secret.description || '',
      value: '', // Always empty - values are write-only
    },
  });

  // Reset form when secret changes
  useEffect(() => {
    if (isOpen) {
      reset({
        secretName: secret.secretName,
        description: secret.description || '',
        value: '',
      });
      setShowValue(false);
    }
  }, [isOpen, secret, reset]);

  const { mutate: updateSecret, isPending } = useUpdateSecret(
    tenantId,
    secret.secretId,
    {
      onSuccess: () => {
        setSuccessMessage('Secret updated successfully!');
        setErrorMessage('');
        setTimeout(() => {
          reset();
          setShowValue(false);
          onClose();
          setSuccessMessage('');
        }, 1500);
      },
      onError: (error) => {
        setErrorMessage(error);
        setSuccessMessage('');
      },
    }
  );

  const onSubmit = (data: UpdateSecretFormData) => {
    setErrorMessage('');
    setSuccessMessage('');

    // Build update DTO (only include non-empty fields)
    const updateData: { secretName?: string; description?: string; value?: string } = {};

    if (data.secretName && data.secretName !== secret.secretName) {
      updateData.secretName = data.secretName;
    }
    if (data.description !== undefined && data.description !== (secret.description || '')) {
      updateData.description = data.description;
    }
    if (data.value && data.value.trim() !== '') {
      updateData.value = data.value;
    }

    // Must have at least one field to update
    if (Object.keys(updateData).length === 0) {
      setErrorMessage('No changes to update');
      return;
    }

    updateSecret(updateData);
  };

  const handleClose = () => {
    if (!isPending) {
      reset();
      setErrorMessage('');
      setSuccessMessage('');
      setShowValue(false);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <Modal.Header>Update Secret</Modal.Header>
      <Modal.Body>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Secret Code (read-only) */}
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-600">
              Secret Code:{' '}
              <CopyableCode value={secret.secretCode} />
            </p>
            <p className="mt-1 text-xs text-gray-500">
              Code is immutable and cannot be changed.
            </p>
          </div>

          {/* Error Message */}
          {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

          {/* Success Message */}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}

          {/* Secret Name */}
          <FormField label="Secret Name" required error={errors.secretName?.message}>
            <Input
              {...register('secretName')}
              placeholder="e.g., Stripe API Key"
              error={!!errors.secretName}
            />
          </FormField>

          {/* Description */}
          <FormField label="Description" error={errors.description?.message}>
            <Textarea
              {...register('description')}
              placeholder="Describe the purpose of this secret..."
              rows={3}
              error={!!errors.description}
            />
          </FormField>

          {/* Secret Value (optional update) */}
          <FormField
            label="New Secret Value"
            error={errors.value?.message}
            helperText="Leave empty to keep the current value. Enter a new value to update it."
          >
            <div className="relative">
              <Input
                {...register('value')}
                type={showValue ? 'text' : 'password'}
                placeholder="Enter new secret value (optional)..."
                error={!!errors.value}
                className="pr-24"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                onClick={() => setShowValue(!showValue)}
              >
                {showValue ? 'Hide' : 'Show'}
              </button>
            </div>
          </FormField>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={isPending}>
              Update Secret
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
